import {
  InstitutionLogo,
  Modal,
  Switch,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { Integration } from "api/mutations/useToggleIntegrationMutation";
import { LogoResponse } from "api/queries/useGetLogoQuery";
import { AxiosResponse } from "axios";
import classNames from "classnames";
import React, { FC, Reducer, useReducer, useState } from "react";

import { calculateFailedResponses } from "components/Onboarding/helpers";

import {
  EnableIntegrationsRequests,
  NoSetupIntegrations,
  useEnableNoSetUpIntegrations,
} from "./hooks/useEnableNoSetUpIntegrations";
import styles from "./Onboarding.module.css";

const { Body, Header, Footer, Section } = Modal;

const { BigCommerce, OracleNetSuite, ClearBooks, PrestaShop, Kashflow } =
  NoSetupIntegrations;

const IntegrationToggle: FC<{
  name: string;
  isIntegrationChecked: boolean;
  updateState: () => void;
  logo: LogoResponse;
}> = ({ name, isIntegrationChecked, updateState, logo }) => (
  <div className={styles.integrationsContainer}>
    <div className={styles.integrationNameWithIcon}>
      <InstitutionLogo
        className={styles.logo}
        institution={{
          logo: {
            src: logo?.logo.square?.image.src || "",
          },
          name,
        }}
      />
      <p>{name}</p>
    </div>
    <Switch
      aria-label={name}
      isChecked={isIntegrationChecked}
      onClick={updateState}
    />
  </div>
);

type IntegrationsState = Record<NoSetupIntegrations, boolean>;

const enableIntegrationsReducer: Reducer<
  IntegrationsState,
  Partial<IntegrationsState>
> = (current, next) => ({ ...current, ...next });

interface IntegrationsWithNoSetupModalProps {
  onSkip: () => void;
  enableIntegrationsRequests: EnableIntegrationsRequests;
}

export const IntegrationsWithNoSetup: FC<IntegrationsWithNoSetupModalProps> = ({
  onSkip,
  enableIntegrationsRequests,
}) => {
  const { addToast } = useToast();
  const [isEnabling, setIsEnabling] = useState<boolean>(false);
  const [integrationsState, dispatch] = useReducer(enableIntegrationsReducer, {
    [ClearBooks]: true,
    [Kashflow]: true,
    [OracleNetSuite]: true,
    [PrestaShop]: true,
    [BigCommerce]: true,
  });

  const {
    toggleBigCommerce,
    toggleClearbooks,
    toggleKashflow,
    toggleOracleNetsuite,
    togglePrestaShop,
    logos,
    sendEvent,
  } = enableIntegrationsRequests;

  const onEnableIntegrations = async () => {
    setIsEnabling(true);

    const result = (await Promise.allSettled(
      [
        integrationsState[ClearBooks]
          ? toggleClearbooks({
              data: { enabled: integrationsState[ClearBooks] },
            })
          : null,
        integrationsState[Kashflow]
          ? toggleKashflow({ data: { enabled: integrationsState[Kashflow] } })
          : null,
        integrationsState[OracleNetSuite]
          ? toggleOracleNetsuite({
              data: { enabled: integrationsState[OracleNetSuite] },
            })
          : null,
        integrationsState[BigCommerce]
          ? toggleBigCommerce({
              data: { enabled: integrationsState[BigCommerce] },
            })
          : null,
        integrationsState[PrestaShop]
          ? togglePrestaShop({
              data: { enabled: integrationsState[PrestaShop] },
            })
          : null,
      ].filter(Boolean)
    )) as PromiseSettledResult<AxiosResponse<Integration, unknown>>[];

    sendEvent(`Enable Integrations Button - Click`, {
      enabledIntegrations: integrationsState,
    });

    setIsEnabling(false);

    if (result.some((result) => result.status === "rejected")) {
      calculateFailedResponses(result).forEach(
        (failedIntegrationName: NoSetupIntegrations) => {
          dispatch({
            [failedIntegrationName]: false,
          });
        }
      );

      addToast({
        message:
          "Some of the integrations failed to enable. Please check your settings and try again.",
        variant: "error",
      });

      return;
    }

    onSkip();
  };

  return (
    <>
      <Header
        onClose={null}
        data-testid="no-setup-modal"
        title={
          <Typography
            as="h1"
            variant="heading_xl"
            className={styles.modalTitle}
            id="no-setup-heading"
          >
            <span>Integrations</span> with no setup
          </Typography>
        }
        subtitle={
          <Typography
            as="p"
            variant="body_sm"
            className={classNames(
              styles.modalSubtitle,
              styles.integrationsWithNoSetupSubtitle
            )}
          >
            These integrations can be launched instantly. Other integrations
            require you to complete an app registration process. Clicking Enable
            integrations will enable those toggled below.
          </Typography>
        }
      />
      <Body variant="dark">
        <Section variant="card">
          <Typography
            as="h3"
            variant="body_md"
            className={classNames(
              styles.modalSubtitle,
              styles.integrationsWithNoSetupSubtitle,
              styles.bold,
              styles.black
            )}
          >
            Accounting integrations
          </Typography>

          <Typography
            as="p"
            variant="body_sm"
            className={classNames(
              styles.modalSubtitle,
              styles.integrationsWithNoSetupSubtitle
            )}
          >
            To see our other accounting integrations and enable them go to&nbsp;
            <strong>Settings &gt; Accounting integrations</strong>
          </Typography>

          <IntegrationToggle
            name={ClearBooks}
            logo={logos.clearBooksLogo}
            isIntegrationChecked={integrationsState[ClearBooks]}
            updateState={() => {
              dispatch({
                [ClearBooks]: !integrationsState[ClearBooks],
              });
            }}
          />

          <IntegrationToggle
            name={Kashflow}
            logo={logos.kashFlowLogo}
            isIntegrationChecked={integrationsState[Kashflow]}
            updateState={() => {
              dispatch({
                [Kashflow]: !integrationsState[Kashflow],
              });
            }}
          />

          <IntegrationToggle
            name={OracleNetSuite}
            logo={logos.oracleNetSuiteLogo}
            isIntegrationChecked={integrationsState[OracleNetSuite]}
            updateState={() => {
              dispatch({
                [OracleNetSuite]: !integrationsState[OracleNetSuite],
              });
            }}
          />
        </Section>

        <Section variant="card">
          <Typography
            as="h3"
            variant="body_md"
            className={classNames(
              styles.modalSubtitle,
              styles.integrationsWithNoSetupSubtitle,
              styles.bold,
              styles.black
            )}
          >
            Commerce integrations
          </Typography>

          <Typography
            as="p"
            variant="body_sm"
            className={classNames(
              styles.modalSubtitle,
              styles.integrationsWithNoSetupSubtitle
            )}
          >
            To see our other commerce integrations and enable them go to&nbsp;
            <strong>Settings &gt; Commerce integrations</strong>
          </Typography>

          <IntegrationToggle
            name={BigCommerce}
            logo={logos.bigCommerceLogo}
            isIntegrationChecked={integrationsState[BigCommerce]}
            updateState={() => {
              dispatch({
                [BigCommerce]: !integrationsState[BigCommerce],
              });
            }}
          />

          <IntegrationToggle
            name={PrestaShop}
            logo={logos.prestaShopLogo}
            isIntegrationChecked={integrationsState[PrestaShop]}
            updateState={() => {
              dispatch({
                [PrestaShop]: !integrationsState[PrestaShop],
              });
            }}
          />
        </Section>
      </Body>
      <Footer
        variant="light"
        buttons={[
          {
            name: "Skip",
            label: "Skip",
            variant: "secondary",
            color: "interface",
            onClick: (event) => {
              event.preventDefault();

              onSkip();
            },
            className: styles.secondaryBtn,
            id: "skip-onboarding-button",
            "data-testid": "skip-button",
          },
          {
            label: "Enable integrations",
            name: "Enable integrations",
            onClick: onEnableIntegrations,
            "data-testid": "enable-integrations-button",
            isLoading: isEnabling,
          },
        ]}
      />
    </>
  );
};

export const IntegrationsWithNoSetupModal: FC<
  Pick<IntegrationsWithNoSetupModalProps, "onSkip">
> = ({ onSkip }) => (
  <IntegrationsWithNoSetup
    onSkip={onSkip}
    enableIntegrationsRequests={useEnableNoSetUpIntegrations()}
  />
);
